import { detectChildType, isElement } from './detectInlineType';
import { InlineImage } from './InlineImage/InlineImage';
import { InlineMap } from './InlineMap/InlineMap';
import { InlineVideo } from './InlineVideo/InlineVideo';
import { InlineYouTube } from './InlineYouTube/InlineYouTube';

import { type DOMNode, type Element } from 'html-react-parser';

export interface InlineContentRendererProps {
  node: Element;
}

type InlineGroup =
  { type: 'image'; elements: Element[] } |
  { type: 'map' | 'youtube' | 'video'; element: Element };

function groupInlineChildren(node: Element): InlineGroup[] {
  const children = (node.children as DOMNode[]).filter(isElement);
  const groups: InlineGroup[] = [];
  let images: Element[] = [];

  const flushImages = () => {
    if (images.length === 0) return;
    groups.push({ type: 'image', elements: [...images] });
    images = [];
  };

  for (const child of children) {
    const type = detectChildType(child);
    if (type === 'image') {
      images.push(child);
    } else {
      flushImages();
      if (type !== 'unsupported') groups.push({ type, element: child });
    }
  }
  flushImages();
  return groups;
}

export function InlineContentRenderer({ node }: InlineContentRendererProps) {
  const groups = groupInlineChildren(node);

  if (groups.length === 0) {
    if (import.meta.env.DEV) console.warn('[InlineContent] unrecognised inline node', node);
    return null;
  }

  return (
    <>
      {groups.map((group, i) => {
        if (group.type === 'image') return group.elements.map((el, j) => <InlineImage key={`${i}-${j}`} element={el} />);
        if (group.type === 'map') return <InlineMap key={i} element={group.element} />;
        if (group.type === 'youtube') return <InlineYouTube key={i} element={group.element} />;
        return <InlineVideo key={i} element={group.element} />;
      })}
    </>
  );
}
